/**
 * EHR Tasks API — backend calls for the EHR staged-task approval page.
 *
 * Read:   getresidentstaged        (all staged resident tasks: add, edit, move, remove)
 *         getusersstaged           (all staged user tasks: add, remove)
 *         core_resident_groups     (groups for the move-in group picker)
 * Write:  resident_add_task        (approve move-in)
 *         resident_edit_task       (approve info update)
 *         resident_move_task       (approve relocation)
 *         resident_remove_task     (approve move-out)
 *         resident_reject_task     (reject any resident task type)
 *         user_reject_task         (reject any user task type)
 *
 * User add/delete approvals route through the existing usersApi.addUser /
 * deleteUsers with taskApproval=true (action: "new-task" / "remove-task"),
 * which clears the matching ext_stg_user row server-side.
 */

import { apiFetch, apiTransaction } from '../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/**
 * Fetch all staged resident tasks from the EHR.
 *
 * V1 procedure: getresidentstaged (stored)
 *
 * Returns an array of 4 arrays:
 *   [0] = add (move-in) tasks
 *   [1] = edit (update info) tasks
 *   [2] = move (relocate) tasks
 *   [3] = remove (move-out) tasks
 */
export function fetchStagedTasks(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'getresidentstaged',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly,
  });
}

/**
 * Fetch all resident groups (for the group picker in the add-task dialog).
 *
 * V1: Tbl: "core_resident_groups", procedure: "fetchbyparameter", reqType: "direct"
 */
export function fetchResidentGroups(campusId) {
  return apiFetch({
    procedure: 'fetchbyparameter',
    reqType: 'direct',
    table: 'core_resident_groups',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/**
 * Fetch all staged user tasks from the EHR.
 *
 * V1 procedure: getusersstaged (stored)
 *
 * Returns an array of 2 arrays:
 *   [0] = add user tasks
 *   [1] = remove user tasks
 */
export function fetchStagedUsers(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'getusersstaged',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly,
  });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * Approve a move-in (add) task.
 *
 * V1 TransType: resident_add_task
 *
 * @param {object} data — full task payload
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function approveAddTask(data, session) {
  return apiTransaction({
    transType: 'resident_add_task',
    payload: [data],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Approve a resident info update (edit) task.
 *
 * V1 TransType: resident_edit_task
 */
export function approveEditTask(data, session) {
  return apiTransaction({
    transType: 'resident_edit_task',
    payload: [data],
    oldPayload: [],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Approve a resident relocation (move) task.
 *
 * V1 TransType: resident_move_task
 */
export function approveMoveTask(data, oldData, session) {
  return apiTransaction({
    transType: 'resident_move_task',
    payload: [data],
    oldPayload: [oldData],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Approve a move-out (remove) task.
 *
 * V1 TransType: resident_remove_task
 */
export function approveRemoveTask(data, session) {
  return apiTransaction({
    transType: 'resident_remove_task',
    payload: [data],
    oldPayload: [],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Reject any staged task (add / edit / move / remove).
 *
 * V1 TransType: resident_reject_task
 *
 * @param {string} cloudId — the cloud_id of the task
 * @param {object} session — { campusId, userId, clientId }
 */
export function rejectTask(cloudId, session) {
  return apiTransaction({
    transType: 'resident_reject_task',
    payload: [{ cloud_id: cloudId, modified_by: session.userId }],
    oldPayload: [],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Reject any staged user task (add / remove).
 *
 * V1 TransType: user_reject_task — clears the matching ext_stg_user row.
 *
 * @param {string} cloudId — the cloud_id of the staged user task
 * @param {object} session — { campusId, userId, clientId }
 */
export function rejectUserTask(cloudId, session) {
  return apiTransaction({
    transType: 'user_reject_task',
    payload: [{ cloud_id: cloudId, modified_by: session.userId }],
    oldPayload: [],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}
